/**
 * 統一頁面配置系統 - 主入口
 *
 * 從各 Remote 和本地模組配置自動生成：
 * - remoteModules（組件列表）
 * - moduleLoaders（動態 import 函數）
 * - pagePermissions（頁面權限表）
 * - sideNavMenus（側邊欄選單）
 */

// ============================================================================
// Re-export Types
// ============================================================================

export * from './types';

// ============================================================================
// Import Module Configs
// ============================================================================

import { config as bigscreenConfig, pages as bigscreenPages } from './bigscreen';
import { config as omConfig, pages as omPages, drawers as omDrawers, services as omServices } from './om';
import { config as hrConfig, pages as hrPages } from './hr';
import { config as essPlannerConfig, pages as essPlannerPages } from './essPlanner';
import { config as managementConfig, pages as managementPages } from './management';
import { config as afuConfig, pages as afuPages } from './afu';
import { toolsPages, fieldPages, dataPages, adminPages, reportPages, inventoryPages, localModules } from './local';

import type {
    PageConfig,
    PagePermissionConfig,
    SideNavMenuItem,
    NavMode,
    RemoteName,
    LocalModuleName,
} from './types';

// ============================================================================
// Export Module Configs
// ============================================================================

export { bigscreenConfig, bigscreenPages };
export { omConfig, omPages, omDrawers, omServices };
export { hrConfig, hrPages };
export { essPlannerConfig, essPlannerPages };
export { managementConfig, managementPages };
export { afuConfig, afuPages };
export { toolsPages, fieldPages, dataPages, adminPages, reportPages, localModules };

/** 所有 Remote 模組配置 */
export const remoteConfigs = {
    om: omConfig,
    bigscreen: bigscreenConfig,
    hr: hrConfig,
    essPlanner: essPlannerConfig,
    management: managementConfig,
    afu: afuConfig,
};

/** 所有頁面（包含 Remote 和本地） */
export const allPages = {
    om: omPages,
    bigscreen: bigscreenPages,
    hr: hrPages,
    essPlanner: essPlannerPages,
    management: managementPages,
    afu: afuPages,
    tools: toolsPages,
    field: fieldPages,
    data: dataPages,
    admin: adminPages,
    report: reportPages,
    inventory: inventoryPages,
};

// ============================================================================
// Generated: remoteModules（組件名稱列表）
// ============================================================================

/**
 * 遠端/本地模組組件映射
 * 用於 MultiViewManager 動態載入
 */
export const remoteModules: Record<string, string[]> = {
    // dispatch 目錄映射到 om（因為 openView 用 /dispatch/XXX 路徑）
    dispatch: ['WorkOrderList', 'WorkOrderDetail', 'DispatchCalendar', 'WorkReport', 'RepairOrderAssistant', 'Dashboard', 'StatisticsPage'],
    om: omPages.map(p => p.name),
    bigscreen: bigscreenPages.map(p => p.name),
    hr: hrPages.map(p => p.name),
    essPlanner: essPlannerPages.map(p => p.name),
    management: managementPages.map(p => p.name),
    afu: afuPages.map(p => p.name),
    tools: toolsPages.map(p => p.name),
    field: fieldPages.map(p => p.name),
    data: dataPages.map(p => p.name),
    admin: adminPages.map(p => p.name),
    report: reportPages.map(p => p.name),
    inventory: inventoryPages.map(p => p.name),
};

// ============================================================================
// NOTE: Module Loaders
// ============================================================================
// 動態 import 無法被 Vite 靜態分析，因此不在此處生成 loader。
// 請使用 menuConfig.ts 中的 getRemoteModuleLoader()，該函數使用硬編碼的 switch/case。
// ============================================================================

// ============================================================================
// Generated: Page Permissions（頁面權限表）
// ============================================================================

/** 本地模式（不需要權限控制，依賴案場權限） */
export const localModes: NavMode[] = ['field', 'data'];

/**
 * 從模組配置生成權限表
 */
function generatePermissionsFromPages(
    moduleName: string,
    pages: PageConfig[]
): Record<string, PagePermissionConfig> {
    const permissions: Record<string, PagePermissionConfig> = {};

    for (const page of pages) {
        // 跳過本地模式（field, data）- 它們依賴案場權限
        if (localModes.includes(page.mode)) continue;

        const key = `${moduleName}:${page.name}`;
        permissions[key] = {
            title: page.title,
            mode: page.mode,
            default: page.default ?? false,
        };
    }

    return permissions;
}

/** 完整頁面權限配置（自動生成） */
export const generatedPagePermissions: Record<string, PagePermissionConfig> = {
    ...generatePermissionsFromPages('om', omPages),
    ...generatePermissionsFromPages('bigscreen', bigscreenPages),
    ...generatePermissionsFromPages('hr', hrPages),
    ...generatePermissionsFromPages('essPlanner', essPlannerPages),
    ...generatePermissionsFromPages('management', managementPages),
    ...generatePermissionsFromPages('afu', afuPages),
    ...generatePermissionsFromPages('tools', toolsPages),
    ...generatePermissionsFromPages('admin', adminPages),
    ...generatePermissionsFromPages('report', reportPages),
    ...generatePermissionsFromPages('inventory', inventoryPages),
    // chat 模式頁面（本地）
    'chat:GroupsView': { title: '群組管理', mode: 'chat', default: false },
    'chat:ChatHistory': { title: '聊天歷史', mode: 'chat', default: false },
    'chat:ChatSettings': { title: '通訊設定', mode: 'chat', default: false },
    // files 模式頁面（預留）
    'files:FileManager': { title: '檔案管理', mode: 'files', default: false },
};

/**
 * 取得預設權限列表
 */
export function getDefaultPermissions(): string[] {
    return Object.entries(generatedPagePermissions)
        .filter(([_, config]) => config.default)
        .map(([key]) => key);
}

// ============================================================================
// Generated: Side Nav Menus（側邊欄選單）
// ============================================================================

/**
 * 從頁面配置生成側邊欄選單
 */
function generateSideNav(
    moduleName: string,
    pages: PageConfig[],
    isLocal: boolean = false
): SideNavMenuItem[] {
    return pages
        .filter(p => p.showInSideNav)
        .map(p => ({
            path: isLocal
                ? `/${moduleName}/${p.name}`
                : `/${moduleName}/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        }));
}

/** 側邊欄選單（自動生成） - 按模式分組 */
export const generatedSideNavMenus: Record<string, SideNavMenuItem[]> = {
    // operation 模式 - 從 om 頁面提取 (mode === 'operation')
    operation: omPages
        .filter(p => p.showInSideNav && p.mode === 'operation')
        .map(p => ({
            path: `/om/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),

    // warroom 模式
    warroom: omPages
        .filter(p => p.showInSideNav && p.mode === 'warroom')
        .map(p => ({
            path: `/om/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),

    // report 模式
    report: omPages
        .filter(p => p.showInSideNav && p.mode === 'report')
        .map(p => ({
            path: `/om/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),

    // org 模式
    org: omPages
        .filter(p => p.showInSideNav && p.mode === 'org')
        .map(p => ({
            path: `/om/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),

    // ess 模式 - 從 om 和 management 合併
    ess: [
        ...omPages.filter(p => p.showInSideNav && p.mode === 'ess').map(p => ({
            path: `/om/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),
        ...managementPages.filter(p => p.showInSideNav && p.mode === 'ess').map(p => ({
            path: `/management/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),
    ],

    // bigscreen 模式
    bigscreen: bigscreenPages
        .filter(p => p.showInSideNav)
        .map(p => ({
            path: `/bigscreen/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),

    // quotation 模式 - 從 essPlanner 提取
    quotation: essPlannerPages
        .filter(p => p.showInSideNav)
        .map(p => ({
            path: `/essPlanner/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),

    // hr 模式
    hr: hrPages
        .filter(p => p.showInSideNav)
        .map(p => ({
            path: `/hr/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),

    // workflow 模式
    workflow: afuPages
        .filter(p => p.showInSideNav)
        .map(p => ({
            path: `/afu/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),

    // management 模式 - 從 management remote + admin local 合併
    management: [
        ...managementPages
            .filter(p => p.showInSideNav && p.mode === 'management')
            .map(p => ({
                path: `/management/${p.name}`,
                title: p.title,
                icon: p.icon || 'icofont-cube',
            })),
        ...adminPages
            .filter(p => p.showInSideNav && p.mode === 'management')
            .map(p => ({
                path: `/admin/${p.name}`,
                title: p.title,
                icon: p.icon || 'icofont-cube',
            })),
    ],

    // tools 模式
    tools: toolsPages
        .filter(p => p.showInSideNav)
        .map(p => ({
            path: `/tools/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),

    // test 模式
    test: managementPages
        .filter(p => p.showInSideNav && p.mode === 'test')
        .map(p => ({
            path: `/management/${p.name}`,
            title: p.title,
            icon: p.icon || 'icofont-cube',
        })),

    // 空的選單（預留或已合併）
    essPlanner: [],
    files: [],
    chat: [
        { path: '/chat/GroupsView', title: '群組管理', icon: 'icofont-users-alt-2' },
        { path: '/chat/ChatHistory', title: '聊天歷史', icon: 'icofont-history' },
        { path: '/chat/ChatSettings', title: '通訊設定', icon: 'icofont-ui-settings' },
    ],
    placeholder: [],
};

// ============================================================================
// Utility: Vite Config Exposes 生成（供各 Remote 使用）
// ============================================================================

/**
 * 生成 vite.config exposes 配置
 * 此函數供各 Remote 專案在 vite.config.ts 中使用
 */
export function generateViteExposes(
    pages: PageConfig[],
    drawers?: Array<{ name: string; path: string }>,
    services?: Array<{ name: string; path: string }>
): Record<string, string> {
    const exposes: Record<string, string> = {};

    // 頁面
    for (const page of pages) {
        exposes[`./${page.name}`] = page.path;
    }

    // Drawer（加上 drawer_ 前綴）
    if (drawers) {
        for (const drawer of drawers) {
            exposes[`./drawer_${drawer.name}`] = drawer.path;
        }
    }

    // 服務
    if (services) {
        for (const service of services) {
            exposes[`./${service.name}`] = service.path;
        }
    }

    return exposes;
}

